import type { ComponentProps } from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import PriceRowFlightPackage from 'LuxKit/PricePoints/PriceRowFlightPackage'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import AgentCommissionPanel from 'agentHub/components/AgentCommissionPanel'
import PayAtPropertyPill from 'components/Common/PayAtPropertyPill'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Group from 'components/utils/Group'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useMetasearchDiscount from 'hooks/useMetasearchDiscount'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import {
  generateLoyaltyPointsCalculatorPropertyOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { getPropertyCheapestHotelOnlyRateForDiscount, getPropertyOfferMapping } from 'selectors/offerPage/offerPageSelectors'

import { getPayAtPropertyDetails } from './singlePropertyUtils'

interface Props {
  property: App.Property.Property
  isPropertyUltraLux: boolean
  duration: number
  regionCode: string
  checkIn?: string
  checkOut?: string
  rooms: Array<App.Occupants>
  hotelOnlyRate: App.EnrichedPropertyRate | App.Property.AnytimeRate
  hotelWithFlightsRate: App.EnrichedPropertyRate | undefined
  cheapestHotelOnlyDiscountRate: App.EnrichedPropertyRate | undefined
  horizontalAlign: 'start' | 'center'
}

function PropertyCheapestRatePriceDetails({
  property,
  isPropertyUltraLux,
  duration,
  regionCode,
  checkIn,
  checkOut,
  rooms,
  hotelOnlyRate,
  hotelWithFlightsRate,
  cheapestHotelOnlyDiscountRate,
  horizontalAlign,
}: Props) {
  const intl = useIntl()
  const isBedbankRate = 'service' in hotelOnlyRate && hotelOnlyRate.service.type === 'bedbank'
  // Discount only when the displayed rate is the anchored rate. A price match alone is unsafe: a
  // different rate can share the price, and the rate list keys the discount by rateId — so it would
  // show here but attach to another package on "view options".
  const shouldApplyDiscount = isBedbankRate &&
    cheapestHotelOnlyDiscountRate &&
    cheapestHotelOnlyDiscountRate.service.propertyId === hotelOnlyRate.service.propertyId &&
    cheapestHotelOnlyDiscountRate.rateId === hotelOnlyRate.rateId
  const [discountAmount, discountFetching] = useMetasearchDiscount(hotelOnlyRate.totals.displayPrice, {
    propertyId: isBedbankRate ? hotelOnlyRate.service.propertyId : undefined,
    checkIn,
    checkOut,
    rooms,
    disabled: !shouldApplyDiscount,
  })

  const payAtPropertyDetails = 'paymentMethod' in hotelOnlyRate
    ? getPayAtPropertyDetails(hotelOnlyRate)
    : undefined

  const memberDiscount = hotelOnlyRate.luxPlusTotals?.discount ?? 0
  const nonMemberDiscount = hotelOnlyRate.totals.discount
  const discount = memberDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? memberDiscount : nonMemberDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? nonMemberDiscount : undefined

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (!('service' in hotelOnlyRate)) return [undefined]
    return [
      generateLoyaltyPointsCalculatorPropertyOptions(hotelOnlyRate, {
        propertyCountryCode: property.location.countryCode,
        propertyState: property.location.region,
        currentRegionCode: regionCode,
        isUltraLux: isPropertyUltraLux,
      }),
    ]
  }, [hotelOnlyRate, property.location.countryCode, property.location.region, regionCode, isPropertyUltraLux])

  const hotelWithFlightsPrice = hotelWithFlightsRate?.includesFlights && typeof hotelWithFlightsRate.cheapestFlightPrice === 'number' && hotelWithFlightsRate.cheapestFlightPrice > 0
    ? hotelWithFlightsRate.luxPlusTotals?.displayPrice ?? hotelWithFlightsRate.totals.displayPrice
    : undefined
  const displayPrice = Math.max(hotelOnlyRate.totals.displayPrice - discountAmount, 0)
  const displayMemberPrice = hotelOnlyRate.luxPlusTotals?.displayPrice ? Math.max(hotelOnlyRate.luxPlusTotals.displayPrice - discountAmount, 0) : undefined

  const agentHubCommissionPanelProps = useMemo<ComponentProps<typeof AgentCommissionPanel> | undefined>(() => {
    // NB: We don't have enough information on anytime rates to accurately determine a commission
    // rate. However, we also no longer show anytime rates (outside of paid marketing traffic), so
    // this isn't really a problem.
    if (!('service' in hotelOnlyRate)) return undefined
    // NB: Because `<AgentCommissionPanel/>` doesn't use min/max commission rates, we don't need to
    // worry about aggregating these across all active offers linked to this property.
    if (hotelOnlyRate.service.type === 'lpc') {
      return {
        offerId: hotelOnlyRate.service.offerId,
        packageId: hotelOnlyRate.service.packageId,
        offerTotal: hotelOnlyRate.totals.sellingPrice,
        retailValue: hotelOnlyRate.totals.valuedUpToPrice,
        memberPrice: hotelOnlyRate.luxPlusTotals?.sellingPrice,
      }
    }
    if (hotelOnlyRate.service.type === 'bedbank') {
      return {
        offerId: hotelOnlyRate.service.propertyId,
        packageId: hotelOnlyRate.service.rateId,
        offerTotal: hotelOnlyRate.totals.sellingPrice,
        retailValue: hotelOnlyRate.totals.valuedUpToPrice,
        memberPrice: hotelOnlyRate.luxPlusTotals?.sellingPrice,
      }
    }
  }, [hotelOnlyRate])

  return <Group direction="vertical" gap={12}>
    <Group direction="horizontal" horizontalAlign={horizontalAlign} gap={4} wrap="wrap" noDisplayWhenEmpty>
      {payAtPropertyDetails?.shouldShowPill && <PayAtPropertyPill hasMultipleOptions={payAtPropertyDetails.shouldShowModal} />}
    </Group>
    <Group direction="vertical" horizontalAlign={horizontalAlign} gap={8}>
      <Group direction="vertical" horizontalAlign={horizontalAlign} gap={4}>
        <Group direction="vertical" horizontalAlign={horizontalAlign}>
          <PriceRowPriceCaption>
            {intl.formatMessage(
              {
                id: 'aB2hOZ7E9REZ6eGA0GLL',
                defaultMessage: '{count, plural, one {# night} other {# nights}} from',
                description: 'Duration in nights for the property summary pricing',
              },
              { count: duration },
            )}
          </PriceRowPriceCaption>
          <LuxPlusPriceStack
            size="L"
            price={displayPrice}
            memberPrice={displayMemberPrice}
            saleUnit="total"
            testid="hotel-summary-price"
            horizontalAlign={horizontalAlign}
          />
          <PriceRowTaxesAndFees />
          <LoadingIndicator visible={discountFetching} floating opaque />
          <PriceRowValueDiscountWithCaption
            size="M"
            originalValue={hotelOnlyRate.luxPlusTotals?.valuedUpToPrice ?? hotelOnlyRate.totals.valuedUpToPrice}
            discountPercentage={discount}
          />
        </Group>
        <LoyaltyPointsEarn
          horizontalAlign={horizontalAlign}
          calculationType="estimate"
          calculationRequests={pointsEarnCalculationRequests}
        />
        {!!hotelWithFlightsPrice && <PriceRowFlightPackage product="hotel" price={hotelWithFlightsPrice} />}
      </Group>
      {agentHubCommissionPanelProps && <AgentCommissionPanel {...agentHubCommissionPanelProps} />}
    </Group>
  </Group>
}

type MappedProps = Pick<Props,
  | 'property'
  | 'isPropertyUltraLux'
  | 'duration'
  | 'regionCode'
  | 'checkIn'
  | 'checkOut'
  | 'rooms'
  | 'cheapestHotelOnlyDiscountRate'
> & { open: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  property: state.property!,
  isPropertyUltraLux: getPropertyOfferMapping(state, appState)?.isUltraLux ?? false,
  duration: state.duration,
  regionCode: appState.geo.currentRegionCode,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  rooms: state.rooms,
  cheapestHotelOnlyDiscountRate: getPropertyCheapestHotelOnlyRateForDiscount(state, appState),
  open: !!state.activeFilter,
}), PropertyCheapestRatePriceDetails, preventRenderWithOpenFilter)
